import { Link } from 'react-router-dom'
import { BadgePercent, FileX, PackageMinus, Tag, Undo2 } from 'lucide-react'

import { t } from '@/i18n'
import { cn, formatCurrency, formatDateTime } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import type { ApprovalRequest, ApprovalStatus, PriceFields } from '@/hooks/useApprovals'

const STATUS_VARIANT: Record<ApprovalStatus, 'warning' | 'default' | 'success' | 'destructive' | 'secondary'> = {
  pending: 'warning',
  processing: 'default',
  applied: 'success',
  rejected: 'destructive',
  cancelled: 'secondary',
}

const STATUS_LABEL: Record<ApprovalStatus, string> = {
  pending: 'approvals.statusPending',
  processing: 'approvals.statusProcessing',
  applied: 'approvals.statusApplied',
  rejected: 'approvals.statusRejected',
  cancelled: 'approvals.statusCancelled',
}

export function ApprovalStatusBadge({ status }: { status: ApprovalStatus }) {
  return <Badge variant={STATUS_VARIANT[status]}>{t(STATUS_LABEL[status])}</Badge>
}

function formatPriceValue(field: keyof PriceFields, value: unknown): string {
  if (field === 'applied_sale_tax') return value ? t('approvals.yes') : t('approvals.no')
  if (field.startsWith('profit_')) return `${Number(value ?? 0)} %`
  return formatCurrency(Number(value ?? 0))
}

function PriceChanges({ requested, previous }: { requested: PriceFields; previous: PriceFields | null }) {
  const fields = (Object.keys(requested) as (keyof PriceFields)[]).filter((field) => {
    const before = previous?.[field]
    const after = requested[field]
    if (typeof after === 'boolean') return Boolean(before) !== after
    return Math.abs(Number(after ?? 0) - Number(before ?? 0)) > 0.01
  })
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
      {fields.map((field) => (
        <div key={field} className="contents">
          <dt className="text-muted-foreground">{t(`approvals.priceField.${field}`)}</dt>
          <dd className="tabular-nums flex flex-wrap gap-x-2">
            <span className="line-through text-muted-foreground">
              {formatPriceValue(field, previous?.[field])}
            </span>
            <span className="font-semibold">{formatPriceValue(field, requested[field])}</span>
          </dd>
        </div>
      ))}
    </dl>
  )
}

interface ApprovalCardProps {
  request: ApprovalRequest
  actions?: React.ReactNode
}

export function ApprovalCard({ request, actions }: ApprovalCardProps) {
  const isDiscount = request.kind === 'discount'
  const isStockOut = request.kind === 'stock_out'
  const isAnnulment = request.kind === 'annulment'
  const isDevolution = request.kind === 'devolution'
  const Icon = isDiscount
    ? BadgePercent
    : isStockOut
      ? PackageMinus
      : isAnnulment
        ? FileX
        : isDevolution
          ? Undo2
          : Tag
  const title = isDiscount
    ? t('approvals.kindDiscount')
    : isStockOut
      ? t('approvals.kindStockOut')
      : isAnnulment
        ? t('approvals.kindAnnulment')
        : isDevolution
          ? t('approvals.kindDevolution')
          : t('approvals.kindPriceChange')

  return (
    <Card className={cn('min-w-0', request.unseen && 'ring-2 ring-primary')}>
      <CardContent className="p-4 space-y-3 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Icon className="h-5 w-5 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-semibold">
                {title}
                {isDiscount && request.discount_percent != null && (
                  <span className="ml-2 text-primary">{request.discount_percent} %</span>
                )}
                {isStockOut && request.quantity != null && (
                  <span className="ml-2 text-destructive tabular-nums">−{request.quantity}</span>
                )}
                {isAnnulment && request.bill_total != null && (
                  <span className="ml-2 text-destructive tabular-nums">−{formatCurrency(request.bill_total)}</span>
                )}
                {isDevolution && request.credit_total != null && (
                  <span className="ml-2 text-destructive tabular-nums">−{formatCurrency(request.credit_total)}</span>
                )}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {request.article_code && <span className="font-mono mr-1">{request.article_code}</span>}
                {request.article_description}
              </p>
            </div>
          </div>
          <div className="shrink-0">
            <ApprovalStatusBadge status={request.status} />
          </div>
        </div>

        {(isDiscount || isAnnulment || isDevolution) && (
          <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            {request.bill_id && (
              <Link to={`/bills/${request.bill_id}`} className="underline underline-offset-2">
                {t('approvals.bill', { number: request.bill_number ?? String(request.bill_id) })}
              </Link>
            )}
            {request.bill_client_name && <span>{request.bill_client_name}</span>}
            {request.line_quantity != null && request.line_unit_price != null && (
              <span className="tabular-nums">
                {t('approvals.lineDetail', {
                  quantity: request.line_quantity,
                  price: formatCurrency(request.line_unit_price),
                })}
              </span>
            )}
          </div>
        )}

        {isDevolution && request.credit_lines && request.credit_lines.length > 0 && (
          <ul className="text-sm space-y-1">
            {request.credit_lines.map((line) => (
              <li key={line.original_line_id} className="flex justify-between gap-3 min-w-0">
                <span className="truncate min-w-0">
                  {line.article_code && <span className="font-mono mr-1">{line.article_code}</span>}
                  {line.description}
                </span>
                <span className="tabular-nums shrink-0">
                  {t('approvals.lineDetail', {
                    quantity: line.quantity,
                    price: formatCurrency(line.unit_price),
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}

        {isStockOut && request.article_stock != null && (
          <p className="text-sm text-muted-foreground tabular-nums">
            {t('approvals.currentStock', { stock: request.article_stock })}
          </p>
        )}

        {request.kind === 'price_change' && request.requested_prices && (
          <PriceChanges requested={request.requested_prices} previous={request.previous_prices} />
        )}

        <div className="text-sm space-y-1">
          <p>
            <span className="text-muted-foreground">
              {t('approvals.requestedBy', { name: request.requested_by_name ?? '—' })} ·{' '}
              {formatDateTime(request.created_at)}
            </span>
            {request.request_note && <span className="block">“{request.request_note}”</span>}
          </p>
          {request.resolved_at && (
            <p>
              <span className="text-muted-foreground">
                {t('approvals.resolvedBy', { name: request.resolved_by_name ?? '—' })} ·{' '}
                {formatDateTime(request.resolved_at)}
              </span>
              {request.resolution_note && <span className="block">“{request.resolution_note}”</span>}
            </p>
          )}
          {request.cancelled_at && (
            <p className={cn(request.status === 'cancelled' && 'text-foreground')}>
              <span className="text-muted-foreground">
                {request.cancelled_by_name
                  ? t('approvals.cancelledBy', { name: request.cancelled_by_name })
                  : t('approvals.cancelledBySystem')}{' '}
                · {formatDateTime(request.cancelled_at)}
              </span>
              {request.cancel_note && <span className="block">“{request.cancel_note}”</span>}
            </p>
          )}
        </div>

        {actions && (
          <div className="flex flex-wrap gap-2 pt-1 [&>button]:flex-1 [&>button]:min-w-[8rem] sm:[&>button]:flex-none">
            {actions}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
